import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  EditButton,
  DeleteButton,
  Create,
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  BooleanField,
  BooleanInput,
  Filter,
  TopToolbar,
  CreateButton,
  required,
  Show,
  SimpleShowLayout,
  FunctionField,
  useRecordContext,
  BulkDeleteButton,
  BulkUpdateButton,
  RichTextField,
  SimpleList,
} from 'react-admin';
import {
  Box,
  Chip,
  Typography,
  Grid,
  Paper,
  useMediaQuery,
  Theme,
} from '@mui/material';
import {
  PushPin as PinIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

// 공지사항 필터
const NoticeFilter = (props: any) => (
  <Filter {...props}>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextInput label="검색 (제목/내용)" source="q" alwaysOn fullWidth />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SelectInput
          source="type"
          label="카테고리"
          choices={[
            { id: 'GENERAL', name: '일반' },
            { id: 'EVENT', name: '이벤트' },
            { id: 'UPDATE', name: '업데이트' },
            { id: 'MAINTENANCE', name: '점검' },
          ]}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <BooleanInput source="isPinned" label="고정 공지만" />
      </Grid>
    </Grid>
  </Filter>
);

// 카테고리 표시 컴포넌트
const NoticeCategoryField = (props: any) => {
  const recordContext = useRecordContext();
  const record = props.record || recordContext;
  if (!record) return null;

  const categoryMap: { [key: string]: { label: string; color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" } } = {
    GENERAL: { label: '일반', color: 'default' },
    EVENT: { label: '이벤트', color: 'primary' },
    UPDATE: { label: '업데이트', color: 'info' },
    MAINTENANCE: { label: '점검', color: 'warning' },
  };

  const category = categoryMap[record.type] || { label: record.type, color: 'default' };
  return <Chip label={category.label} color={category.color} size="small" />;
};


// 조회수 표시 컴포넌트
const ViewCountField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <ViewIcon fontSize="small" color="action" />
      <Typography variant="body2">
        {record.viewCount?.toLocaleString() || 0}
      </Typography>
    </Box>
  );
};

// 리스트 액션 툴바
const NoticeListActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

// 벌크 액션 버튼
const NoticeBulkActions = () => (
  <>
    <BulkUpdateButton
      label="일괄 고정"
      data={{ isPinned: true }}
      mutationMode="pessimistic"
    />
    <BulkUpdateButton
      label="일괄 고정 해제"
      data={{ isPinned: false }}
      mutationMode="pessimistic"
    />
    <BulkDeleteButton mutationMode="pessimistic" />
  </>
);

// 공지사항 목록
export const NoticeList = () => {
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <>
      <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E', fontSize: { xs: '1.5rem', md: '2rem' } }}>
          공지사항
        </Typography>
      </Box>
      <List
        title="공지사항"
        filters={<NoticeFilter />}
        actions={<NoticeListActions />}
        bulkActionButtons={<NoticeBulkActions />}
        perPage={25}
        sort={{ field: 'createdAt', order: 'DESC' }}
      >
        {isSmall ? (
          <SimpleList
            primaryText={(record) => record.title}
            secondaryText={(record) => (
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <NoticeCategoryField record={record} />
                <Typography variant="caption" color="textSecondary">
                  {new Date(record.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            tertiaryText={(record) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                {record.isPinned && <PinIcon color="primary" fontSize="small" />}
                <Typography variant="caption" color="textSecondary">
                  조회 {record.viewCount || 0}
                </Typography>
              </Box>
            )}
            linkType="edit"
          />
        ) : (
          <Datagrid rowClick="edit">
            <FunctionField
              label="고정"
              render={(record: any) => (
                record.isPinned ? <PinIcon color="primary" /> : null
              )}
            />
            <TextField source="title" label="제목" />
            <NoticeCategoryField label="카테고리" />
            <ViewCountField label="조회수" />
            <DateField source="createdAt" label="작성일" showTime />
            <EditButton />
            <DeleteButton />
          </Datagrid>
        )}
      </List>
    </>
  );
};

// 공지사항 생성
export const NoticeCreate = () => (
  <Create>
    <SimpleForm>
      <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
        기본 정보
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextInput
            source="title"
            label="제목"
            validate={required()}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectInput
            source="type"
            label="카테고리"
            choices={[
              { id: 'GENERAL', name: '일반' },
              { id: 'EVENT', name: '이벤트' },
              { id: 'UPDATE', name: '업데이트' },
              { id: 'MAINTENANCE', name: '점검' },
            ]}
            validate={required()}
            defaultValue="GENERAL"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
        내용
      </Typography>

      <TextInput
        source="content"
        label="공지사항 내용"
        validate={required()}
        multiline
        rows={10}
        fullWidth
      />

      <Typography variant="h6" sx={{ mt: 3, mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
        설정
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <BooleanInput source="isPinned" label="상단 고정" defaultValue={false} />
        </Grid>
      </Grid>
    </SimpleForm>
  </Create>
);

// 공지사항 수정
export const NoticeEdit = () => {
  const NoticeTitle = () => {
    const record = useRecordContext();
    return <span>공지사항 수정: {record?.title}</span>;
  };

  return (
    <Edit title={<NoticeTitle />}>
      <SimpleForm>
        <Typography variant="h6" sx={{ mb: 2 }}>
          기본 정보
        </Typography>

        <TextInput source="id" label="ID" disabled />

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextInput
              source="title"
              label="제목"
              validate={required()}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SelectInput
              source="type"
              label="카테고리"
              choices={[
                { id: 'GENERAL', name: '일반' },
                { id: 'EVENT', name: '이벤트' },
                { id: 'UPDATE', name: '업데이트' },
                { id: 'MAINTENANCE', name: '점검' },
              ]}
              validate={required()}
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          내용
        </Typography>

        <TextInput
          source="content"
          label="공지사항 내용"
          validate={required()}
          multiline
          rows={10}
          fullWidth
        />

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          설정
        </Typography>

        <BooleanInput source="isPinned" label="상단 고정" />

        <Typography variant="h6" sx={{ mt: 3, mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
          통계 정보
        </Typography>

        <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>조회수</Typography>
              <NumberField source="viewCount" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>작성일</Typography>
              <DateField source="createdAt" showTime />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>수정일</Typography>
              <DateField source="updatedAt" showTime />
            </Grid>
          </Grid>
        </Paper>
      </SimpleForm>
    </Edit>
  );
};

// 공지사항 상세보기
export const NoticeShow = () => (
  <Show>
    <SimpleShowLayout sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>공지사항 정보</Typography>

          <TextField source="id" label="ID" />
          <TextField source="title" label="제목" />
          <NoticeCategoryField label="카테고리" />
          <BooleanField source="isPinned" label="상단 고정" />

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontSize: { xs: '1rem', md: '1.25rem' } }}>
            내용
          </Typography>
          <RichTextField source="content" />
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>통계 정보</Typography>

          <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ViewIcon fontSize="small" color="action" />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>조회수:</Typography>
              <NumberField source="viewCount" />
            </Box>
          </Paper>

          <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
              메타 정보
            </Typography>
            <DateField source="createdAt" label="작성일" showTime />
            <DateField source="updatedAt" label="수정일" showTime />
          </Paper>
        </Grid>
      </Grid>
    </SimpleShowLayout>
  </Show>
);